import { Component, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { LoadingComponent } from '../shared/components/loading/loading.component';
import { ToastComponent } from '../shared/components/toast/toast.component';
import { inject, OnInit, OnDestroy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthService } from '../core/services/auth.service';
import { BusinessContextService } from '../core/services/business-context.service';
import { SocketService } from '../core/services/socket.service';
import { ToastService } from '../core/services/toast.service';
import { Subscription, combineLatest, map } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, LoadingComponent, ToastComponent],
  template: ` <loading></loading>
    <toast></toast>
    <router-outlet></router-outlet>`,
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('dentium-web');
  
  private authService = inject(AuthService);
  private contextService = inject(BusinessContextService);
  private socketService = inject(SocketService);
  private toastService = inject(ToastService);
  private subscriptions: Subscription[] = [];
  
  // Capturar contexto de inyección para toObservable
  private activeBusiness$ = toObservable(this.contextService.activeBusiness);

  ngOnInit() {
    this.initSocketSubscriptions();
    this.initNotificationListeners();
    this.refreshSessionData();
  }

  /**
   * Refresca silenciosamente los datos de sesión al recargar la página.
   * Carga el perfil global para SuperAdmins y los permisos del negocio activo.
   * Deduplicación de peticiones manejada a nivel de AuthService.
   */
  private refreshSessionData() {
    if (!this.authService.hasSession()) return;

    // 1. Refrescar usuario (permisos globales/SuperAdmin)
    this.authService.getAccount().subscribe({
      error: (err) => console.error('Silent refresh user failed', err)
    });
    
    // 2. Refrescar permisos del negocio activo si lo hay
    if (this.contextService.hasContext()) {
      this.authService.getMyBusinesses().subscribe({
          next: (response: any) => {
             const list = response.businesses ?? [];
             const activeId = this.contextService.business();
             const updated = list.find((b: any) => 
                 (b.business || b._id || b.id) === activeId
             );
             if (updated) {
                 // Actualiza los permisos en el contexto local (reactivo)
                 this.contextService.selectFromRaw(updated, response.isSuperAdmin ?? false);
             }
          },
          error: (err) => console.error('Silent refresh business failed', err)
      });
    }
  }

  private initSocketSubscriptions() {
    // Escuchar cambios en usuario y negocio para actualizar salas de socket
    const sub = combineLatest([
      this.authService.currentUser$,
      this.activeBusiness$
    ]).subscribe(([user, context]) => {
      if (user) {
        this.socketService.joinRooms({
          userId: user.id || (user as any)._id,
          businessId: context?.business,
          establishmentId: context?.invoiceEstablishmentId,
          pointOfEmissionId: context?.invoiceEmissionPointId
        });
      }
    });
    this.subscriptions.push(sub);
  }

  private seenNotifications = new Set<string>();

  private initNotificationListeners() {
    // Escuchar notificaciones personales
    const userSub = this.socketService.onNotification().subscribe(notif => {
      this.showToastOnce(notif);
    });

    // Escuchar logs de negocio (Bitácora)
    const businessSub = this.socketService.onBusinessLog().subscribe(log => {
      // Solo mostramos logs importantes como Toasts
      if (log.type === 'success' || log.type === 'error' || log.type === 'warning') {
        this.showToastOnce(log);
      }
    });

    this.subscriptions.push(userSub, businessSub);
  }

  private showToastOnce(data: any) {
    const id = data._id || (data as any).id;
    if (!id) {
      this.toastService.show(data.title, data.message, data.type || 'info');
      return;
    }

    if (this.seenNotifications.has(id)) return;

    this.seenNotifications.add(id);
    this.toastService.show(data.title, data.message, data.type || 'info');

    // Limpiar después de 5 segundos para ahorrar memoria
    setTimeout(() => this.seenNotifications.delete(id), 5000);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
